import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    fetchWatchlistContextStatus,
    stageWatchlistContextRefresh,
    stageFactorDatasetSelection,
    updateRiskContext,
} from '../../../utils/ApiCall';
import { parseCloudRunError } from '../../msiDriftReviewHelpers';
import {
    contextUpdateKey,
    factorDataUpdatedNotice,
    factorDataUpdateKey,
    factorVendorSwitchedNotice,
    factorVendorSwitchFailedMessage,
    factorVendorSwitchReplacedMessage,
    factorVendorSwitchWaitingMessage,
    hasFactorDataUpdate,
    isFactorVendorSwitchRunning,
} from '../../../context/factorDataSyncPolicy';
import { formatUserFactorModel } from '../../../utils/factorDataOptions';
import {
    announceContextRefreshApproval,
    applyStagedContextRefresh,
} from '../../../utils/watchlistContextRefreshApproval';

// An approved Watchlist change is still being applied; the update starts once
// it finishes. Never an error: a pending change never blocks factor data.
const WAITING_FOR_UPDATE_CODE = 'watchlist_context_refresh_waiting_for_update';
const WAITING_FOR_UPDATE_NOTICE = 'Your Watchlist change is still being applied. The new factor data is used once it finishes.';
// The update ended without publishing because another change published first
// (or replaced it); the next check runs it again against the new Watchlist.
const REPLACED_MESSAGE_CODES = new Set(['watchlist_update_stale', 'watchlist_update_superseded']);

function selectionRetryIsAmbiguous(error, parsed) {
    const statusCode = error?.statusCode ?? parsed.statusCode;
    return error?.retryable !== false && (statusCode == null
        || statusCode === 408 || statusCode === 429 || statusCode >= 500);
}

/** Keep the lightweight context authority current without mounting the Watchlist grid. */
export default function useWatchlistContextSync({
    autoSyncEnabled,
    preferencesReady = true,
    operationActive = false,
    operationStatus,
    publicationState,
    optimizerSummaryStatus = 'complete',
    userUid,
    watchlistVersion,
    sessionScopeToken,
    // Changes when a saved user setting changes the Watchlist's factor
    // context (the missing-factor policy or a factor model), so the check
    // runs right away.
    factorSettingKey = '',
    onReviewHandoff,
    onError,
    onNotice,
}) {
    const [statusSnapshot, setStatusSnapshot] = useState(null);
    const status = statusSnapshot?.status ?? null;
    const [busy, setBusy] = useState(false);
    const [preparingFactorUpdate, setPreparingFactorUpdate] = useState(false);
    const [dismissedKey, setDismissedKey] = useState('');
    const [forcePrompt, setForcePrompt] = useState(false);
    const stageAttemptRef = useRef(null);
    // Checks and approvals in flight: a second check waits for any of them,
    // but a staged approval is never dropped for a check in flight, since it
    // has one attempt id per revision and approving it again is harmless.
    const inFlightRef = useRef(new Set());
    const [appliedRevisionId, setAppliedRevisionId] = useState(null);
    const appliedRevisionScopeRef = useRef(null);
    const appliedContextRef = useRef(null);
    const announceUpdateRef = useRef(false);
    // The exact update the user chose to use, or declined at its review. A
    // used update runs again by itself when a published change replaced it.
    const acceptedKeyRef = useRef('');
    const declinedKeyRef = useRef('');
    // The Settings vendor switch this session approved: { target, current,
    // revisionId, waiting }. Its outcome is announced in vendor terms, and one
    // that a running Watchlist change kept waiting, or replaced, is submitted
    // again once no change is running. Only this browser session holds it, and
    // its vendor is saved only when it publishes.
    const vendorSwitchRef = useRef(null);
    const vendorSwitchSubmitting = useRef(false);
    const datasetAttemptRef = useRef(null);
    const datasetSelectionRef = useRef(null);
    const readErrorReported = useRef(false);
    const readSequenceRef = useRef(0);
    const scopeKey = `${userUid || ''}:${sessionScopeToken || ''}`;
    // Retain the last status for ongoing update UI. Only the exact current
    // application context may authorize new Performance reads.
    const statusContextKey = JSON.stringify([scopeKey, watchlistVersion, factorSettingKey]);
    const statusContextOwnerRef = useRef({ key: statusContextKey });
    if (statusContextOwnerRef.current.key !== statusContextKey) {
        statusContextOwnerRef.current = { key: statusContextKey };
    }
    const statusContextOwner = statusContextOwnerRef.current;
    const currentStatus = statusSnapshot?.owner === statusContextOwner ? status : null;
    const activeScopeKeyRef = useRef(scopeKey);
    activeScopeKeyRef.current = scopeKey;
    const autoSyncRef = useRef(autoSyncEnabled);
    autoSyncRef.current = preferencesReady && autoSyncEnabled;

    // ``staged`` is a re-check the server already staged, approved here exactly
    // as one staged by this call.
    const stageRefresh = useCallback(async (context, { apply = false, automatic = false, staged = null } = {}) => {
        if (!context || !sessionScopeToken) return;
        const datasetSelection = Boolean(staged?.factor_dataset_selection);
        if (apply && !automatic && !datasetSelection) {
            // Recorded even while another check is in flight, so the user's
            // choice still runs at the next check.
            acceptedKeyRef.current = contextUpdateKey(context.available);
            if (declinedKeyRef.current === acceptedKeyRef.current) declinedKeyRef.current = '';
        }
        const flightKey = staged ? `approval:${staged.watchlist_revision_id}` : 'check';
        if (inFlightRef.current.has(flightKey) || (!staged && inFlightRef.current.size > 0)) return;
        const attemptScopeKey = activeScopeKeyRef.current;
        const attemptStatusOwner = statusContextOwner;
        inFlightRef.current.add(flightKey);
        const attemptKey = `${userUid}:${watchlistVersion}:${JSON.stringify(context.available)}`;
        if (stageAttemptRef.current?.key !== attemptKey) {
            stageAttemptRef.current = { key: attemptKey, id: crypto.randomUUID(), finished: false, retryAt: 0 };
        }
        const attempt = stageAttemptRef.current;
        // The saved context can still report "current" while this explicit
        // vendor/dataset approval is in flight. Show progress from the intent.
        if (datasetSelection || hasFactorDataUpdate(context)) setPreparingFactorUpdate(true);
        setBusy(true);
        try {
            const result = staged || await stageWatchlistContextRefresh(attempt.id);
            if (activeScopeKeyRef.current !== attemptScopeKey) return;
            if (result?.status === 'existing_work'
                && ['queued', 'running'].includes(result.work_status)) {
                attempt.finished = true;
                appliedRevisionScopeRef.current = attemptScopeKey;
                setAppliedRevisionId(result.watchlist_revision_id);
                appliedContextRef.current = context.available;
                announceUpdateRef.current = datasetSelection || hasFactorDataUpdate(context);
                onNotice?.('A Watchlist refresh is already in progress. Open Imports to see its status.');
                return;
            }
            if (result?.status !== 'pending_review' || !result?.watchlist_revision_id) {
                throw Object.assign(new Error('The Watchlist refresh did not return a review.'), { retryable: false });
            }
            if (apply) {
                // Staging rereads the available context. A manual click approves
                // the displayed dataset, not a different release published in flight.
                const accepted = contextUpdateKey(result.context?.available) === acceptedKeyRef.current;
                if ((!automatic && factorDataUpdateKey(result.context) !== factorDataUpdateKey(context))
                    || (automatic && hasFactorDataUpdate(result.context) && !autoSyncRef.current && !accepted)) {
                    if (statusContextOwnerRef.current === attemptStatusOwner) {
                        setStatusSnapshot({ owner: attemptStatusOwner, status: result.context });
                    }
                    setForcePrompt(true);
                    attempt.finished = true;
                    return;
                }
                const lifecycle = await applyStagedContextRefresh(result);
                if (activeScopeKeyRef.current !== attemptScopeKey) return;
                attempt.finished = true;
                appliedRevisionScopeRef.current = attemptScopeKey;
                setAppliedRevisionId(result.watchlist_revision_id);
                appliedContextRef.current = result.context?.available || context.available;
                announceUpdateRef.current = datasetSelection || hasFactorDataUpdate(context);
                announceContextRefreshApproval(lifecycle, sessionScopeToken);
                setDismissedKey(factorDataUpdateKey(context));
                setForcePrompt(false);
                return true;
            }
            attempt.finished = true;
            onReviewHandoff?.({
                origin: 'watchlist_context_refresh',
                revisionId: result.watchlist_revision_id,
                status: 'changes_awaiting_review',
                sessionScopeToken,
            });
        } catch (caught) {
            if (activeScopeKeyRef.current !== attemptScopeKey) return;
            const parsed = parseCloudRunError(caught);
            const statusCode = caught?.statusCode ?? parsed.statusCode;
            const code = caught?.code ?? parsed.code;
            if (datasetSelection) {
                // A failed explicit selection never becomes permission to adopt
                // the shared latest dataset on a later automatic check.
                attempt.finished = true;
                datasetSelectionRef.current = null;
                if (!selectionRetryIsAmbiguous(caught, parsed)) datasetAttemptRef.current = null;
                onError?.(parsed.message || 'The selected factor data could not be loaded. Your previous dataset and Watchlist remain active.');
                return false;
            }
            const vendorSwitch = staged
                && vendorSwitchRef.current?.revisionId === staged.watchlist_revision_id
                ? vendorSwitchRef.current : null;
            if (vendorSwitch) {
                attempt.finished = true;
                if (code === WAITING_FOR_UPDATE_CODE) {
                    // Submitted again once the approved change has finished.
                    vendorSwitch.waiting = true;
                    onNotice?.(factorVendorSwitchWaitingMessage(vendorSwitch.target, vendorSwitch.current));
                } else {
                    vendorSwitchRef.current = null;
                    onError?.(parsed.message || factorVendorSwitchFailedMessage(vendorSwitch.target, vendorSwitch.current));
                }
                return;
            }
            if (code === WAITING_FOR_UPDATE_CODE) {
                // Starts on a later check, once the approved change has finished.
                attempt.finished = false;
                attempt.retryAt = Date.now() + 60000;
                if (!automatic) {
                    setDismissedKey(factorDataUpdateKey(context));
                    setForcePrompt(false);
                    onNotice?.(WAITING_FOR_UPDATE_NOTICE);
                }
                return;
            }
            const retryable = caught?.retryable !== false && (statusCode == null
                || statusCode === 408 || statusCode === 429 || statusCode >= 500);
            attempt.finished = !retryable;
            attempt.retryAt = Date.now() + 60000;
            if (hasFactorDataUpdate(context) && (!automatic || !retryable)) setForcePrompt(true);
            if (!automatic || !attempt.errorReported) {
                onError?.(parsed.message || 'Could not prepare the data update.');
                attempt.errorReported = true;
            }
        } finally {
            if (activeScopeKeyRef.current === attemptScopeKey) {
                inFlightRef.current.delete(flightKey);
                setBusy(inFlightRef.current.size > 0);
                if (inFlightRef.current.size === 0) setPreparingFactorUpdate(false);
            }
        }
    }, [onError, onNotice, onReviewHandoff, sessionScopeToken, statusContextOwner, userUid, watchlistVersion]);

    const isDateSelectionBlocked = useCallback(() => Boolean(
        operationActive || inFlightRef.current.size || vendorSwitchRef.current
    ), [operationActive]);
    const selectFactorDataset = useCallback(async ({ selectionMode, releaseId, expectedRevisionId }) => {
        if (!sessionScopeToken || !userUid) return false;
        if (isDateSelectionBlocked()) {
            onError?.('Wait for the current Watchlist update to finish, then choose the factor date again.');
            return false;
        }
        const requestScope = activeScopeKeyRef.current;
        const key = JSON.stringify([userUid, expectedRevisionId, selectionMode, releaseId]);
        if (datasetAttemptRef.current?.key !== key) {
            datasetAttemptRef.current = { key, id: crypto.randomUUID() };
        }
        inFlightRef.current.add('dataset-selection');
        acceptedKeyRef.current = '';
        setForcePrompt(false);
        // Progress shows from the approval, as for a vendor switch, while
        // the selected date's re-check is still being staged.
        setPreparingFactorUpdate(true);
        setBusy(true);
        try {
            const staged = await stageFactorDatasetSelection({
                attemptId: datasetAttemptRef.current.id,
                expectedRevisionId, selectionMode, releaseId,
            });
            if (activeScopeKeyRef.current !== requestScope) return false;
            if (!staged?.context?.available || !staged?.factor_dataset_selection
                || staged.factor_dataset_selection.selection_mode !== selectionMode
                || (selectionMode === 'pinned'
                    && Number(staged.factor_dataset_selection.factor_release_id) !== Number(releaseId))) {
                throw Object.assign(new Error('The selected dataset could not be verified. Refresh the available dates and try again.'), { retryable: false });
            }
            datasetSelectionRef.current = staged.watchlist_revision_id;
            return await stageRefresh(staged.context, {
                apply: staged.context.requires_initial_validation !== true, staged,
            });
        } catch (caught) {
            if (activeScopeKeyRef.current !== requestScope) return false;
            const parsed = parseCloudRunError(caught);
            if (!selectionRetryIsAmbiguous(caught, parsed)) datasetAttemptRef.current = null;
            onError?.(parsed.message || 'The selected factor data could not be loaded. Your previous dataset and Watchlist remain active.');
            return false;
        } finally {
            if (activeScopeKeyRef.current === requestScope) {
                inFlightRef.current.delete('dataset-selection');
                setBusy(inFlightRef.current.size > 0);
                if (inFlightRef.current.size === 0) setPreparingFactorUpdate(false);
            }
        }
    }, [isDateSelectionBlocked, onError, sessionScopeToken, stageRefresh, userUid]);

    // Saving another factor vendor in User Settings approves that vendor's
    // data, exactly as "Use new factor data" does: the re-check the save
    // staged is approved now. One staged while an approved Watchlist change
    // was still running is submitted again once that change finishes. A
    // Watchlist saved before context tracking opens its review instead, as
    // its own check always does. The vendor is saved when the switch publishes.
    const applyFactorVendorChange = useCallback((transition) => {
        const target = transition?.target_factor_model;
        if (!target) return null;
        const current = transition.previous_factor_model ?? transition.factor_model;
        if (transition.status === 'waiting_for_watchlist_update') {
            vendorSwitchRef.current = { target, current, revisionId: null, waiting: true };
            return 'waiting';
        }
        const staged = transition.successor?.watchlist_revision_id ? transition.successor : null;
        const context = staged?.context;
        if (!context?.available) return null;
        vendorSwitchRef.current = {
            target, current, revisionId: staged.watchlist_revision_id, waiting: false,
        };
        const review = context.requires_initial_validation === true;
        void stageRefresh(context, { apply: !review, staged });
        return review ? 'review' : 'apply';
    }, [stageRefresh]);

    // Stages the waiting switch again for the Watchlist now published, through
    // the same Settings call, and approves it.
    const resubmitVendorSwitch = useCallback(async () => {
        const pending = vendorSwitchRef.current;
        if (!pending?.waiting || vendorSwitchSubmitting.current) return;
        const attemptScopeKey = activeScopeKeyRef.current;
        vendorSwitchSubmitting.current = true;
        pending.waiting = false;
        try {
            const response = await updateRiskContext({ factorModel: pending.target });
            if (activeScopeKeyRef.current !== attemptScopeKey
                || vendorSwitchRef.current !== pending) return;
            if (!response || response.success === false) {
                vendorSwitchRef.current = null;
                onError?.(response?.message
                    || factorVendorSwitchFailedMessage(pending.target, pending.current));
                return;
            }
            const transition = response.factor_vendor_transition;
            if (!applyFactorVendorChange(transition)) vendorSwitchRef.current = null;
        } finally {
            vendorSwitchSubmitting.current = false;
        }
    }, [applyFactorVendorChange, onError]);

    useEffect(() => {
        setStatusSnapshot(null);
        setBusy(false);
        setPreparingFactorUpdate(false);
        stageAttemptRef.current = null;
        inFlightRef.current = new Set();
        setAppliedRevisionId(null);
        appliedRevisionScopeRef.current = null;
        appliedContextRef.current = null;
        announceUpdateRef.current = false;
        acceptedKeyRef.current = '';
        declinedKeyRef.current = '';
        vendorSwitchRef.current = null;
        datasetAttemptRef.current = null;
        datasetSelectionRef.current = null;
        readErrorReported.current = false;
        readSequenceRef.current += 1;
        setDismissedKey('');
        setForcePrompt(false);
    }, [scopeKey]);

    useEffect(() => {
        // A deliberate new Load after a declined/failed review is a new attempt,
        // including when review handoff cleared appliedRevisionId already.
        if (datasetSelectionRef.current === operationStatus?.revisionId
            && (operationStatus.cancelled
                || REPLACED_MESSAGE_CODES.has(operationStatus.messageCode)
                || ['success', 'failed', 'partial'].includes(operationStatus.topLevelStatus))) {
            datasetAttemptRef.current = null;
        }
    }, [operationStatus]);

    const appliedPublicationCommitted = Boolean(appliedRevisionId
        && publicationState?.revisionId === appliedRevisionId && publicationState.committed === true);
    useEffect(() => {
        if (!appliedRevisionId || appliedRevisionScopeRef.current !== scopeKey) return;
        const hasMatchingOperation = operationStatus?.revisionId === appliedRevisionId;
        const vendorSwitch = vendorSwitchRef.current?.revisionId === appliedRevisionId
            ? vendorSwitchRef.current : null;
        // Durable success is projected as running/99 until client publication.
        // Its exact failed handoff must end factor progress even while that
        // projection remains, or after the transient status has cleared. Keep
        // the coordinator reservation and previously accepted data untouched.
        if (publicationState?.revisionId === appliedRevisionId
            && publicationState.lifecycle === 'needs_attention') {
            onError?.('The factor data update was saved, but the app could not finish refreshing. Reload the page to use the updated data.');
            if (vendorSwitch) vendorSwitchRef.current = null;
            setAppliedRevisionId(null);
            return;
        }
        if (!hasMatchingOperation && !appliedPublicationCommitted) return;
        if (hasMatchingOperation && operationStatus.phase === 'removal_review') {
            // The app shell owns the review prompt across every route, including
            // reloads. Stop the spinner without opening a tab-local History dialog.
            vendorSwitchRef.current = null;
            setAppliedRevisionId(null);
        } else if ((hasMatchingOperation && operationStatus.topLevelStatus === 'success')
            || (!hasMatchingOperation && appliedPublicationCommitted)) {
            // The exact coordinator commit outlives the transient operation
            // status. Keep progress until its risk data and optimizer checks finish.
            if (publicationState?.revisionId !== appliedRevisionId) return;
            if (!publicationState.committed) return;
            if (['pending', 'loading'].includes(optimizerSummaryStatus)) return;
            if (['error', 'superseded'].includes(optimizerSummaryStatus)) {
                onError?.('The factor data update was saved, but optimized portfolio checks could not finish. Reload the page to check your saved portfolios.');
                if (vendorSwitch) vendorSwitchRef.current = null;
                setAppliedRevisionId(null);
                return;
            }
            if (vendorSwitch) onNotice?.(factorVendorSwitchedNotice(vendorSwitch.target));
            else if (announceUpdateRef.current) onNotice?.(factorDataUpdatedNotice(appliedContextRef.current));
            if (vendorSwitch) vendorSwitchRef.current = null;
            setAppliedRevisionId(null);
        } else if (operationStatus.cancelled || REPLACED_MESSAGE_CODES.has(operationStatus.messageCode)) {
            // Declined at its review, or replaced by a change that published
            // first. Nothing changed; a replaced update runs again by itself,
            // and a replaced vendor switch is submitted again for the new
            // Watchlist once no change is running.
            if (vendorSwitch && !operationStatus.cancelled) {
                vendorSwitch.waiting = true;
                onNotice?.(factorVendorSwitchReplacedMessage(vendorSwitch.target, vendorSwitch.current));
            } else if (vendorSwitch) {
                vendorSwitchRef.current = null;
            }
            if (datasetSelectionRef.current === appliedRevisionId) {
                datasetSelectionRef.current = null;
                acceptedKeyRef.current = '';
                if (!operationStatus.cancelled) {
                    onNotice?.('Your Watchlist changed before the selected factor data could be loaded. Choose the date again to re-check the updated Watchlist.');
                }
            }
            setAppliedRevisionId(null);
        } else if (['failed', 'partial'].includes(operationStatus.topLevelStatus)) {
            onError?.(vendorSwitch
                ? factorVendorSwitchFailedMessage(vendorSwitch.target, vendorSwitch.current)
                : 'The data update did not complete. Your previous context remains active.');
            if (vendorSwitch) vendorSwitchRef.current = null;
            setAppliedRevisionId(null);
        }
    }, [appliedPublicationCommitted, appliedRevisionId, onError, onNotice, onReviewHandoff, operationStatus, optimizerSummaryStatus, publicationState, scopeKey, sessionScopeToken]);

    useEffect(() => {
        if (!userUid) return undefined;
        let cancelled = false;
        // Poll and visibility wakes share this effect's pending read. A new
        // context effect owns an independent guard and starts immediately.
        let readInFlight = false;
        const refresh = async () => {
            if (readInFlight) return;
            readInFlight = true;
            const readSequence = ++readSequenceRef.current;
            const readContextOwner = statusContextOwner;
            try {
                const next = await fetchWatchlistContextStatus();
                if (cancelled || readSequenceRef.current !== readSequence
                    || statusContextOwnerRef.current !== readContextOwner) return;
                readErrorReported.current = false;
                setStatusSnapshot({ owner: readContextOwner, status: next });
                if (!operationActive && vendorSwitchRef.current?.waiting) {
                    void resubmitVendorSwitch();
                    return;
                }
                const initialValidation = next?.requires_initial_validation === true;
                const updateKey = contextUpdateKey(next?.available);
                const shouldPrepareReview = next?.status === 'update_available'
                    && !operationActive
                    && !isFactorVendorSwitchRunning(next?.factor_vendor_switch)
                    && updateKey !== declinedKeyRef.current
                    && ((preferencesReady && autoSyncEnabled) || !hasFactorDataUpdate(next)
                        || initialValidation || updateKey === acceptedKeyRef.current);
                if (shouldPrepareReview) {
                    const key = `${userUid}:${watchlistVersion}:${JSON.stringify(next.available)}`;
                    const attempt = stageAttemptRef.current;
                    if (attempt?.key !== key || (!attempt.finished && Date.now() >= attempt.retryAt)) {
                        void stageRefresh(next, { apply: !initialValidation, automatic: true });
                    }
                }
            } catch (caught) {
                if (cancelled || readSequenceRef.current !== readSequence
                    || statusContextOwnerRef.current !== readContextOwner) return;
                const message = 'Could not check the latest Watchlist context. The app will retry automatically.';
                setStatusSnapshot({ owner: readContextOwner, status: { status: 'read_error', message } });
                if (!readErrorReported.current) {
                    readErrorReported.current = true;
                    onError?.(message, caught);
                }
            } finally {
                readInFlight = false;
            }
        };
        void refresh();
        const timer = window.setInterval(refresh, 60000);
        const onVisible = () => { if (document.visibilityState === 'visible') void refresh(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [
        autoSyncEnabled, onError, operationActive, preferencesReady, statusContextOwner,
        resubmitVendorSwitch, stageRefresh, userUid, watchlistVersion,
    ]);

    const updateInProgress = Boolean(
        (busy && (preparingFactorUpdate || hasFactorDataUpdate(status)))
        || (appliedRevisionId && announceUpdateRef.current),
    );
    const switchingVendor = vendorSwitchRef.current;
    const updateProgressMessage = !appliedRevisionId
        ? switchingVendor
            ? `Preparing ${formatUserFactorModel(switchingVendor.target)}…`
            : 'Preparing update…'
        : appliedPublicationCommitted
            ? 'Checking portfolios…'
            : operationStatus?.revisionId === appliedRevisionId && operationStatus.topLevelStatus === 'success'
                ? 'Refreshing risk…'
                : switchingVendor
                    ? `Switching to ${formatUserFactorModel(switchingVendor.target)}…`
                    : 'Updating factor data…';

    const updateProgressDetail = appliedPublicationCommitted
        ? 'Your updated factor data is active; finishing portfolio checks.'
        : 'The factor chip shows the data currently displayed.';
    // Presentation only. The existing app-shell operation and accepted UI
    // publication remain the authority for progress and terminal outcomes.
    const progressContext = useMemo(() => updateInProgress ? {
        userUid, sessionScopeToken, revisionId: appliedRevisionId,
        message: updateProgressMessage, detail: updateProgressDetail,
    } : null, [appliedRevisionId, sessionScopeToken, updateInProgress, updateProgressDetail, updateProgressMessage, userUid]);

    return {
        status, currentStatus, busy, stageRefresh,
        dateSelectionBlocked: isDateSelectionBlocked(),
        updateInProgress, updateProgressMessage, updateProgressDetail, progressContext,
        promptOpen: !operationActive && hasFactorDataUpdate(status)
            && (forcePrompt || ((!preferencesReady || !autoSyncEnabled) && dismissedKey !== factorDataUpdateKey(status))),
        showPrompt: () => setForcePrompt(true),
        dismissPrompt: () => { setDismissedKey(factorDataUpdateKey(status)); setForcePrompt(false); },
        applyUpdate: () => stageRefresh(status, { apply: true }),
        applyFactorVendorChange,
        selectFactorDataset,
        // The user kept the current Watchlist at the update's review: it is
        // not run again by itself, but stays available to use later.
        declineUpdate: (available) => {
            declinedKeyRef.current = contextUpdateKey(available);
            if (acceptedKeyRef.current === declinedKeyRef.current) acceptedKeyRef.current = '';
            setDismissedKey(factorDataUpdateKey({ available }));
            setForcePrompt(false);
        },
    };
}
